import { Vect2D } from 'components/Images/ZoomAndPanImg'
import { useAppController } from 'customHooks/useAppController'
import { newProjectActions } from 'features/BillOfMaterials/store/projectReducer'
import { isEqual } from 'lodash'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { QF_Events, QF_EventsArgs } from 'model/QF_Events'
import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import {
  BomItemCheckListItem,
  PdfViewerModel,
  SvgShape,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppDispatch, useAppSelector } from 'store/configureStore'
import { ProjectState } from 'store/Project/ProjectTypes'
import { useDeepCompareMemoize } from 'utils/useDeepCompareMemoize'
import { useShowException } from 'utils/useShowException'
import { BomItemDetailsTabTypes } from '../../BomItemDetailsDialog/DetailsTabs/BomItemDetailsTabs'
import { EngDrawingsRef } from '../PdfDrawings/Carrousel/EngDrawingCarrousel'
import { getHighlightCenterCoordinates } from '../PdfDrawings/getHighlightCenterCoordinates'
import { DrawingCheckListController } from './DrawingCheckListController'

function useDrawingChecklistController() {
  const { controller, loading } = useAppController(() => {
    return new DrawingCheckListController()
  })

  const ShowException = useShowException('check list')

  return {
    controller,
    loading,
    ShowException,
  }
}

function useChecklistData(bomItemPointer: BomItemPointer) {
  const {
    checkList,
    activeFeatureId,
    shallScrollToActiveItem,
  }: {
    checkList?: ProjectState['currentCheckList']['checkList']
    activeFeatureId?: ProjectState['currentCheckList']['activeFeatureId']
    shallScrollToActiveItem?: ProjectState['currentCheckList']['shallScrollToActiveItem']
  } = useAppSelector((state) => {
    const checkListBomItemId =
      state.project.currentCheckList?.bomItemPointer?.id

    if (!checkListBomItemId || checkListBomItemId !== bomItemPointer?.id) {
      return {
        checkList: null,
        activeFeatureId: null,
        shallScrollToActiveItem: false,
      }
    }

    return state.project.currentCheckList || {}
  }, isEqual)

  const positionFix = useRef<
    | {
        position: Vect2D
        scale: number
        attachmentId: string
      }
    | undefined
  >(undefined)

  const setPositionFix = useCallback(
    (position?: Vect2D, scale?: number, attachmentId?: string) => {
      if (position && scale) {
        positionFix.current = { position, scale, attachmentId }
      } else {
        positionFix.current = undefined
      }
    },
    [],
  )

  const [filter, setFilter] = useState<string>('')

  const [filteredCheckList, setFilteredCheckList] =
    useState<Record<string, BomItemCheckListItem[]>>(checkList)

  useEffect(() => {
    if (!checkList) {
      return
    }

    const filtered = Object.values(checkList).flatMap((items) => {
      return items.filter((item) => {
        const includedInFeatureName = item.featureName
          .toLowerCase()
          .includes(filter.toLowerCase())
        const includedInDescription = item.description
          .toLowerCase()
          .includes(filter.toLowerCase())
        const includedInCustomDescription = item.customDescription
          ?.toLowerCase()
          .includes(filter.toLowerCase())

        return (
          includedInFeatureName ||
          includedInDescription ||
          includedInCustomDescription
        )
      })
    })

    const groupedFiltered = filtered.reduce(
      (acc, item) => {
        const key = item.category || 'default'

        if (!acc[key]) {
          acc[key] = []
        }

        acc[key].push(item)

        return acc
      },
      {} as Record<string, BomItemCheckListItem[]>,
    )

    setFilteredCheckList(groupedFiltered)
  }, [filter, checkList])

  const shallUpdateCheckListFlag = useAppSelector((state) => {
    return state.project.currentCheckList?.shallUpdateCheckList || false
  })

  return {
    checkList,
    activeFeatureId,
    positionFix,
    setPositionFix,
    filter,
    setFilter,
    filteredCheckList,
    shallScrollToActiveItem,
    shallUpdateCheckListFlag,
  }
}

function useChecklistDispatch(bomItemPointer: BomItemPointer) {
  const dispatch = useAppDispatch()

  const updateCheckListStore = useCallback(
    (checklist: Record<string, BomItemCheckListItem[]>) => {
      dispatch(
        newProjectActions.setProjectStateProperties({
          currentCheckList: {
            bomItemPointer: bomItemPointer,
            checkList: checklist,
          },
        }),
      )
    },
    [bomItemPointer, dispatch],
  )

  const setAsActiveCheckListItem = useCallback(
    (itemId: string, shallScrollToActiveItem: boolean) => {
      dispatch(
        newProjectActions.setProjectStateProperties({
          currentCheckList: {
            activeFeatureId: itemId,
            shallScrollToActiveItem,
          },
        } as Partial<ProjectState>),
      )
    },
    [dispatch],
  )

  const setShallUpdateCheckListFlag = useCallback(
    (shallUpdate: boolean) => {
      dispatch(
        newProjectActions.setProjectStateProperties({
          currentCheckList: {
            shallUpdateCheckList: shallUpdate,
          },
        } as Partial<ProjectState>),
      )
    },
    [dispatch],
  )

  return {
    updateCheckListStore,
    setAsActiveCheckListItem,
    setShallUpdateCheckListFlag,
  }
}

export function getShapeScaleAndPosition(
  shape: SvgShape,
  carrouselRef: RefObject<EngDrawingsRef>,
): {
  scale: number | undefined
  position: Vect2D | undefined
} {
  if (!shape) {
    console.warn('no shape provided')
    return { scale: 1, position: { x: 0, y: 0 } }
  }

  let newScale = undefined
  let newPosition = undefined

  switch (shape.shape) {
    case 'polygon': {
      const center = getHighlightCenterCoordinates(shape)
      const coords = shape['points'].map((pointString: string) => {
        const point = pointString.split(',')
        return {
          x: parseFloat(point[0]),
          y: parseFloat(point[1]),
        }
      })

      const minX = Math.min(...coords.map((point) => point.x))
      const maxX = Math.max(...coords.map((point) => point.x))
      const minY = Math.min(...coords.map((point) => point.y))
      const maxY = Math.max(...coords.map((point) => point.y))

      const width = maxX - minX
      const height = maxY - minY

      const imgWidth = carrouselRef.current?.getImgWidth()
      const imgHeight = carrouselRef.current?.getImgHeight()

      if (Math.max(width, height) < 500) {
        // for smaller items, we can calculate the scale based on shape size
        newScale = Math.max(width, height) / Math.min(width, height) //* 0.1
      } else {
        // for larger items, we can calculate the scale based on image size
        if (width >= height) {
          newScale = (imgWidth / width) * 0.1
        } else {
          newScale = (imgHeight / height) * 0.1
        }
      }

      // fix to not zoom too much or too little
      if (newScale > 5) {
        newScale = newScale * 0.1
      } else if (newScale > 1) {
        newScale = newScale * 0.5
      }

      newPosition = center

      break
    }
    case 'rect': {
      const center = getHighlightCenterCoordinates(shape)
      const width = shape['width']
      const height = shape['height']

      const imgWidth = carrouselRef.current?.getImgWidth()
      const imgHeight = carrouselRef.current?.getImgHeight()

      if (Math.max(width, height) < 500) {
        // for smaller items, we can calculate the scale based on shape size
        newScale = Math.max(width, height) / Math.min(width, height) //* 0.1
      } else {
        // for larger items, we can calculate the scale based on image size
        if (width >= height) {
          newScale = (imgWidth / width) * 0.1
        } else {
          newScale = (imgHeight / height) * 0.1
        }
      }

      // fix to not zoom too much or too little
      if (newScale > 5) {
        newScale = newScale * 0.1
      } else if (newScale > 1) {
        newScale = newScale * 0.5
      }

      newPosition = center

      break
    }
    default: {
      console.warn('shape of type ' + shape.shape + ' not supported')
      return { scale: 1, position: { x: 0, y: 0 } }
    }
  }

  return { scale: newScale, position: newPosition }
}

export function useDrawingChecklistCallbacks(
  bomItemPointer: BomItemPointer,
  contentTab?: BomItemDetailsTabTypes,
  setContentTab?: (tab: BomItemDetailsTabTypes) => void,
) {
  const { controller, loading, ShowException } = useDrawingChecklistController()
  const {
    checkList,
    activeFeatureId,
    shallScrollToActiveItem,
    positionFix,
    setPositionFix,
    filter,
    setFilter,
    filteredCheckList,
    shallUpdateCheckListFlag,
  } = useChecklistData(bomItemPointer)

  const { setAsActiveCheckListItem, setShallUpdateCheckListFlag } =
    useChecklistDispatch(bomItemPointer)

  const _bomItemPointer = useDeepCompareMemoize(bomItemPointer)

  const handleGetCheckList = useCallback(async () => {
    try {
      if (_bomItemPointer?.id) {
        await controller.GetBomItemCheckList(_bomItemPointer)
      }
    } catch (error) {
      ShowException(error)
    }
  }, [ShowException, _bomItemPointer, controller])

  // const debouncedGetCheckList = useCallback(() => {
  //   return debounce(handleGetCheckList, 300, { leading: false, trailing: true })
  // }, [handleGetCheckList])

  const handleAddCheckListItemFromHighlight = useCallback(
    async (event: Event) => {
      const ev = event as CustomEvent<QF_EventsArgs['PDFHighlightClicked']>

      const existingCheckListItem = Object.values(checkList || {})
        .flat()
        .find((x) => x.featureId === ev.detail.featureId)

      if (existingCheckListItem) {
        // duplicate item, set it as active
        setAsActiveCheckListItem(existingCheckListItem.featureId, true)
        return
      }

      try {
        let checkListItem: BomItemCheckListItem = {
          itemId: undefined,
          engineeringDrawingId: ev.detail.pdfPage.pageId,
          bomItemId: bomItemPointer.id,
          featureId: ev.detail.featureId,
          description: ev.detail.pdfFeature.clipboardContent,
          done: false,
          category: ev.detail.pdfFeature.category,
          customDescription: undefined,
          featureName: ev.detail.pdfFeature.name,
        }

        const returnedItemId = await controller.AddBomItemCheckListItem(
          bomItemPointer,
          checkListItem,
        )

        checkListItem = {
          ...checkListItem,
          itemId: returnedItemId,
        }
      } catch (err) {
        ShowException(err)
      }
    },
    [
      ShowException,
      bomItemPointer,
      checkList,
      controller,
      setAsActiveCheckListItem,
    ],
  )

  const handleDismissTodo = useCallback(
    async (itemId: string, itemCategory: string) => {
      try {
        await controller.DeleteBomItemCheckListItem(
          bomItemPointer,
          itemId,
          itemCategory,
        )
      } catch (err) {
        ShowException(err)
        handleGetCheckList()
      }
    },
    [ShowException, bomItemPointer, controller, handleGetCheckList],
  )

  const handleReactivateTodo = useCallback(
    async (itemId: string, itemCategory: string) => {
      try {
        await controller.UpdateBomItemCheckListItem(
          bomItemPointer,
          itemId,
          itemCategory,
          {
            done: false,
          },
        )
      } catch (err) {
        ShowException(err)
        handleGetCheckList()
      }
    },
    [ShowException, bomItemPointer, controller, handleGetCheckList],
  )

  const handleCompleteTodo = useCallback(
    async (itemId: string, itemCategory: string) => {
      try {
        await controller.UpdateBomItemCheckListItem(
          bomItemPointer,
          itemId,
          itemCategory,
          {
            done: true,
          },
        )
      } catch (err) {
        ShowException(err)
        handleGetCheckList()
      }
    },
    [ShowException, bomItemPointer, controller, handleGetCheckList],
  )

  const handlePdfLoaded = useCallback(
    (event: Event, carrouselRef: RefObject<EngDrawingsRef>) => {
      const ev = event as CustomEvent<QF_EventsArgs['PDFViewerLoaded']>

      if (ev.detail.isLoading < 1) {
        return
      }

      if (
        positionFix.current &&
        positionFix.current.attachmentId ===
          carrouselRef.current.getCurrentPageId()
      ) {
        const fix = { ...positionFix.current }

        requestIdleCallback(
          () => {
            carrouselRef.current?.setZoomAndPosition(fix.scale, fix.position)
          },
          {
            timeout: 1000,
          },
        )

        setPositionFix(undefined)
      }
    },
    [positionFix, setPositionFix],
  )

  const handleZoomToFeature = useCallback(
    (
      checklistItem: BomItemCheckListItem,
      carrouselRef: RefObject<EngDrawingsRef>,
      pdfViewModel: PdfViewerModel,
      handleChangeDrawing: (attachmentId: string, pageNumber: number) => void,
    ) => {
      const zoomToFeature = (shouldSetPositionFix?: boolean) => {
        setAsActiveCheckListItem(checklistItem.featureId, false)

        // get the highlights to have all the coordinates
        const page = pdfViewModel?.pages.find(
          (page) =>
            page.features.findIndex((feature) => {
              return feature.id === checklistItem.featureId
            }) > -1,
        )

        if (!page) {
          console.warn(
            'No page found for attachmentId',
            checklistItem.engineeringDrawingId,
          )
          return
        }

        const feature = page.features?.find((feature) => {
          return feature.id === checklistItem.featureId
        })

        const highlights = feature?.highlights

        if (!highlights?.length) {
          console.warn('No highlights found for featureId')

          return
        }

        const shape = highlights[0]

        let { scale: newScale, position: newPosition } =
          getShapeScaleAndPosition(shape, carrouselRef)

        if (feature.category.toLowerCase() === 'gd&t') {
          // GD&T features needs to show not only the shape but also some context around it.
          // So we set a more zoomed out scale. .45 is a good default, but could be improved if we
          // had the bounding box of the GD&T feature including all the symbols.
          newScale = 0.45
        }

        if (pdfViewModel.pages.length < 2) {
          carrouselRef.current.setZoomAndPosition(newScale, newPosition)
          return
        }

        if (
          shouldSetPositionFix ||
          carrouselRef.current.getCurrentPageId() !==
            checklistItem.engineeringDrawingId ||
          carrouselRef.current.getCurrentPageNumber() !== page.pageNumber
        ) {
          setPositionFix(
            newPosition,
            newScale,
            checklistItem.engineeringDrawingId,
          )
          handleChangeDrawing(
            checklistItem.engineeringDrawingId,
            page.pageNumber,
          )
        } else {
          carrouselRef.current.setZoomAndPosition(newScale, newPosition)
        }
      }

      if (setContentTab && contentTab !== 'drawings') {
        setContentTab('drawings')
        setTimeout(() => {
          zoomToFeature(true)
        }, 1500)
      } else {
        zoomToFeature()
      }
    },
    [contentTab, setAsActiveCheckListItem, setContentTab, setPositionFix],
  )

  const handleSubmitCustomDescription = useCallback(
    async (
      itemId: string,
      itemCateogory: string,
      newValues: {
        customDescription: string
      },
      oldValue: BomItemCheckListItem,
    ): Promise<string> => {
      try {
        await controller.UpdateBomItemCheckListItem(
          bomItemPointer,
          itemId,
          itemCateogory,
          {
            customDescription: newValues.customDescription,
          },
        )

        if (newValues.customDescription === '') {
          return oldValue.description
        } else {
          return newValues.customDescription
        }
      } catch (err) {
        ShowException(err)
        return oldValue.description
      }
    },
    [ShowException, bomItemPointer, controller],
  )

  const handleChangeFilter = useCallback(
    (filterValue: string) => {
      setFilter(filterValue)
    },
    [setFilter],
  )

  return {
    checkList,
    activeFeatureId,
    handleGetCheckList,
    handleAddCheckListItemFromHighlight,
    handleDismissTodo,
    handlePdfLoaded,
    handleZoomToFeature,
    handleCompleteTodo,
    handleReactivateTodo,
    handleSubmitCustomDescription,
    loading,
    filter,
    handleChangeFilter,
    filteredCheckList,
    shallScrollToActiveItem,
    shallUpdateCheckListFlag,
    setShallUpdateCheckListFlag,
    // debouncedGetCheckList,
  }
}

export function useDrawingChecklistEffects(
  bomItemPointer: BomItemPointer,
  contentTab: BomItemDetailsTabTypes,
  setContentTab: (tab: BomItemDetailsTabTypes) => void,
  bomItemExists: boolean,
  carrouselRef?: RefObject<EngDrawingsRef>,
) {
  const {
    handleGetCheckList,
    handleAddCheckListItemFromHighlight,
    handlePdfLoaded,
    handleZoomToFeature,
    handleDismissTodo,
    handleCompleteTodo,
    handleReactivateTodo,
    handleSubmitCustomDescription,
    loading,
    checkList,
    activeFeatureId,
    shallScrollToActiveItem,
    handleChangeFilter,
    filter,
    filteredCheckList,
    shallUpdateCheckListFlag,
    setShallUpdateCheckListFlag,
    // debouncedGetCheckList,
  } = useDrawingChecklistCallbacks(bomItemPointer, contentTab, setContentTab)

  useEffect(() => {
    if (bomItemExists) {
      handleGetCheckList()
    }
  }, [bomItemExists, handleGetCheckList])

  useEffect(() => {
    if (bomItemExists && shallUpdateCheckListFlag) {
      handleGetCheckList()
      setShallUpdateCheckListFlag(false)
    }
  }, [
    bomItemExists,
    handleGetCheckList,
    setShallUpdateCheckListFlag,
    shallUpdateCheckListFlag,
  ])

  const listening = useRef(false)

  useEffect(() => {
    const ctr = new AbortController()

    if (!listening.current) {
      listening.current = true

      document.addEventListener(
        QF_Events.PDFHighlightClicked,
        handleAddCheckListItemFromHighlight,
        {
          signal: ctr.signal,
        },
      )
    }

    return () => {
      ctr.abort()
      document.removeEventListener(
        QF_Events.PDFHighlightClicked,
        handleAddCheckListItemFromHighlight,
      )
      listening.current = false
    }
  }, [handleAddCheckListItemFromHighlight])

  useEffect(() => {
    const ctr = new AbortController()

    document.addEventListener(
      QF_Events.PDFViewerLoaded,
      (ev) => handlePdfLoaded(ev, carrouselRef),
      { signal: ctr.signal },
    )

    return () => {
      ctr.abort()
    }
  }, [handlePdfLoaded, carrouselRef])

  const activeCheckListItemRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (
      activeFeatureId &&
      shallScrollToActiveItem &&
      activeCheckListItemRef.current
    ) {
      activeCheckListItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [activeFeatureId, shallScrollToActiveItem])

  return {
    checkList,
    handleZoomToFeature,
    handleDismissTodo,
    handleCompleteTodo,
    handleReactivateTodo,
    handleSubmitCustomDescription,
    activeFeatureId,
    activeCheckListItemRef,
    loading,
    handleChangeFilter,
    filter,
    filteredCheckList,
  }
}
